import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, CheckCircle2, ClipboardCheck, FileBarChart, Globe2, Handshake, Layers, LineChart, Lock, MessagesSquare, ScanSearch, Send, ShieldCheck, Target, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { api, errMsg, useFetch } from "../lib/api";
import { trackRewardlanceEvent, slug, countryCode } from "../lib/analytics";
import { useI18n } from "../i18n";
import { Wrap, Eyebrow, H2, Arrow, QA } from "./Public";

const COUNTRIES = ["United Kingdom", "United States", "Canada", "Ireland", "Germany", "France", "Spain", "Italy", "Netherlands", "Belgium", "Sweden", "Norway", "Denmark", "Switzerland", "Austria", "Poland", "Portugal", "United Arab Emirates", "Saudi Arabia", "Qatar", "Kuwait", "Egypt", "Morocco", "Tunisia", "Algeria", "South Africa", "Nigeria", "Kenya", "India", "Pakistan", "Singapore", "Malaysia", "Thailand", "Vietnam", "Australia", "New Zealand", "Japan", "Brazil", "Mexico", "Argentina", "Colombia"];

function Hero({ s }) {
  const { p } = useI18n();
  return (
    <Wrap className="pt-16 lg:pt-24 pb-12">
      <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr] items-end">
        <div>
          <Eyebrow>{s.eyebrow}</Eyebrow>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1]">{s.title}</h1>
          <p className="mt-6 text-muted-foreground max-w-2xl text-base">{s.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-full" data-testid="solutions-hero-cta"><a href="#enquiry">{s.cta} <Arrow /></a></Button>
            <Button asChild size="lg" variant="outline" className="rounded-full" data-testid="solutions-how-link"><Link to={p("/how-it-works")}>{s.how}</Link></Button>
          </div>
        </div>
        <Card className="p-6 rounded-2xl bg-[#0B1224] text-white border-0 space-y-4" data-testid="solutions-hero-card">
          {s.heroCard.map((x, i) => { const I = [Users, Layers, Globe2, FileBarChart][i]; return <div key={x} className="flex items-center gap-3"><span className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center"><I className="h-4 w-4" /></span><span className="font-medium">{x}</span></div>; })}
        </Card>
      </div>
    </Wrap>
  );
}

function Grid({ g, icons, testid }) {
  return (
    <Wrap className="py-14" data-testid={testid}>
      <Eyebrow>{g.eyebrow}</Eyebrow><H2>{g.title}</H2>
      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {g.items.map(([tt, d], i) => { const I = icons[i]; return <Card key={tt} className="p-6 rounded-2xl transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-md"><I className="h-6 w-6 text-primary" /><h3 className="mt-4 font-semibold">{tt}</h3><p className="mt-2 text-sm text-muted-foreground leading-relaxed">{d}</p></Card>; })}
      </div>
    </Wrap>
  );
}

function Steps({ g }) {
  return (
    <Wrap className="py-14">
      <Eyebrow>{g.eyebrow}</Eyebrow><H2>{g.title}</H2>
      <ol className="mt-10 grid gap-6 md:grid-cols-4">
        {g.items.map(([tt, d], i) => <li key={tt} className="relative border-t-2 border-primary pt-5"><span className="font-mono text-sm text-primary">0{i + 1}</span><h3 className="mt-2 font-semibold">{tt}</h3><p className="mt-2 text-sm text-muted-foreground leading-relaxed">{d}</p></li>)}
      </ol>
    </Wrap>
  );
}

const F = ({ l, req, children, className = "" }) => <div className={`space-y-1.5 ${className}`}><Label className="text-sm">{l}{req && <span className="text-primary"> *</span>}</Label>{children}</div>;
const EMPTY = { first_name: "", last_name: "", country: "", phone: "", email: "", company: "", categories: [], other: false, other_category: "", message: "" };

function Services({ v, set, f }) {
  const { t } = useI18n();
  const { data } = useFetch("/public/categories");
  const toggle = (n) => set({ ...v, categories: v.categories.includes(n) ? v.categories.filter((x) => x !== n) : [...v.categories, n] });
  const chip = (on) => `rounded-full border px-3.5 py-1.5 text-sm transition-colors ${on ? "bg-primary text-white border-primary" : "hover:border-primary/60"}`;
  return (
    <F l={f.services} req className="sm:col-span-2">
      <div className="flex flex-wrap gap-2" data-testid="enquiry-categories">
        {(data || []).map((c) => <button type="button" key={c.id} onClick={() => toggle(c.name)} className={chip(v.categories.includes(c.name))} aria-pressed={v.categories.includes(c.name)} data-testid={`enquiry-cat-${c.id}`}>{t.categories.names[c.name]?.[0] || c.name}</button>)}
        <button type="button" onClick={() => set({ ...v, other: !v.other })} className={chip(v.other)} aria-pressed={v.other} data-testid="enquiry-cat-other">{f.other}</button>
      </div>
      {v.other && <Input className="mt-2" placeholder={f.otherPh} value={v.other_category} onChange={(e) => set({ ...v, other_category: e.target.value })} data-testid="enquiry-other-input" />}
    </F>
  );
}

function ThankYou({ name, f }) {
  const { p } = useI18n();
  return (
    <div className="text-center py-10 space-y-4" data-testid="enquiry-success">
      <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
      <h3 className="font-display text-2xl font-bold">{f.thanks} {name}.</h3>
      <p className="text-muted-foreground max-w-md mx-auto">{f.thanksBody}</p>
      <Button asChild variant="outline" className="rounded-full"><Link to={p("/how-it-works")}>{f.seeHow}</Link></Button>
    </div>
  );
}

function EnquiryForm({ f }) {
  const { p } = useI18n();
  const [v, setV] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const inp = (k, props = {}) => <Input value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} required data-testid={`enquiry-${k.replace("_", "-")}-input`} {...props} />;
  const submit = async (e) => {
    e.preventDefault();
    if (!v.categories.length && !(v.other && v.other_category.trim())) { toast.error(f.pickOne); return; }
    setBusy(true);
    try {
      await api.post("/public/business-enquiry", { ...v, other_category: v.other ? v.other_category : "" });
      const services = [...v.categories.map(slug), ...(v.other && v.other_category.trim() ? ["other"] : [])];
      const cc = countryCode(v.country);
      trackRewardlanceEvent("generate_lead", { lead_source: "website", form_name: "proposal_form", services_chosen: [...new Set(services)].join(","), ...(cc ? { country: cc } : {}) });
      setDone(v.first_name);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (done) return <ThankYou name={done} f={f} />;
  return (
    <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2" data-testid="enquiry-form">
      <F l={f.first} req>{inp("first_name", { autoComplete: "given-name" })}</F>
      <F l={f.last} req>{inp("last_name", { autoComplete: "family-name" })}</F>
      <F l={f.email} req>{inp("email", { type: "email", autoComplete: "email", dir: "ltr" })}</F>
      <F l={f.phone} req>{inp("phone", { type: "tel", autoComplete: "tel", placeholder: "+44 ...", dir: "ltr", pattern: "[0-9+()\\-\\s.]{6,30}" })}</F>
      <F l={f.company} req>{inp("company", { autoComplete: "organization" })}</F>
      <F l={f.country} req>{inp("country", { list: "enquiry-countries", autoComplete: "country-name", placeholder: f.countryPh })}<datalist id="enquiry-countries">{COUNTRIES.map((c) => <option key={c} value={c} />)}</datalist></F>
      <Services v={v} set={setV} f={f} />
      <F l={f.message} req className="sm:col-span-2"><Textarea rows={5} minLength={10} required placeholder={f.messagePh} value={v.message} onChange={(e) => setV({ ...v, message: e.target.value })} data-testid="enquiry-message-input" /></F>
      <div className="sm:col-span-2 flex flex-wrap items-center gap-4">
        <Button type="submit" size="lg" className="rounded-full" disabled={busy} data-testid="enquiry-submit-button"><Send className="h-4 w-4 me-2 rtl:-scale-x-100" />{busy ? f.sending : f.submit}</Button>
        <p className="text-xs text-muted-foreground max-w-md">{f.consent} <Link to={p("/privacy")} className="underline">{f.privacy}</Link>.</p>
      </div>
    </form>
  );
}

function Enquiry({ f }) {
  return (
    <Wrap className="py-14 scroll-mt-20">
      <div id="enquiry" className="grid gap-10 lg:grid-cols-[1fr_1.6fr] scroll-mt-24">
        <div>
          <Eyebrow>{f.eyebrow}</Eyebrow>
          <H2>{f.title}</H2>
          <p className="mt-4 text-muted-foreground leading-relaxed">{f.body}</p>
          <ul className="mt-6 space-y-3 text-sm">{f.points.map((x) => <li key={x} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-primary mt-0.5" />{x}</li>)}</ul>
          <p className="mt-8 text-sm text-muted-foreground">{f.preferEmail} <a href="mailto:info@rewardlance.com" className="text-primary font-medium">info@rewardlance.com</a></p>
        </div>
        <Card className="p-6 sm:p-8 rounded-2xl shadow-sm"><EnquiryForm f={f} /></Card>
      </div>
    </Wrap>
  );
}

export function Pricing() {
  const { t } = useI18n();
  const s = t.solutions;
  useEffect(() => {
    if (window.location.hash === "#enquiry") { setTimeout(() => document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth" }), 150); }
  }, []);
  return (
    <div data-testid="solutions-page">
      <Hero s={s} />
      <Grid g={s.outcomes} icons={[ScanSearch, MessagesSquare, ClipboardCheck, Target]} testid="solutions-outcomes" />
      <Steps g={s.steps} />
      <Grid g={s.trust} icons={[BadgeCheck, ShieldCheck, Lock, Handshake]} testid="solutions-trust" />
      <Wrap className="py-6"><Card className="p-6 rounded-2xl flex flex-col sm:flex-row sm:items-center gap-4 bg-primary/5 border-primary/20"><LineChart className="h-8 w-8 text-primary shrink-0" /><p className="text-sm sm:text-base flex-1">{s.banner}</p><Button asChild className="rounded-full"><a href="#enquiry">{s.bannerCta}</a></Button></Card></Wrap>
      <Wrap className="py-14 max-w-4xl"><H2>{s.faq.title}</H2><div className="mt-6"><QA items={s.faq.items} id="biz" /></div></Wrap>
      <Enquiry f={s.form} />
    </div>
  );
}
